import { useEffect, useState } from "react";
import RightTimeApi from "@/API";
import { FaPlusSquare } from "react-icons/fa";
import { CiEdit } from "react-icons/ci";
import { MdCheckBox, MdCheckBoxOutlineBlank } from "react-icons/md";
import { toast } from "react-toastify";import EmailTemplateEditModal from "@modals/EmailTemplateEditModal";
import type { CompanyEmailTabData, CompanySystemEmailTemplate, EmailTemplate } from "@/utils/emailTemplate";

export default function CompanyEmailTab(props: { tabData: { activeTab?: string; company?: { id: number }; updateSpinner?: (v: boolean) => void } }) {
  const { activeTab, company, updateSpinner } = props.tabData;
  const [tabData, setTabData] = useState<CompanyEmailTabData | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);
  const [isEditOpen, setIsEditOpen] = useState(false);

  useEffect(() => {
    if (activeTab === "email" && company?.id) {
      loadTabDataAsync();
    }
  }, [activeTab, company?.id]);

  async function loadTabDataAsync() {
    if (!company?.id) return;
    updateSpinner?.(true);
    try {
      const resp = await RightTimeApi.getAsync(`/Company/${company.id}/Email`);
      if (resp?.ok && resp.jsonData) {
        setTabData(resp.jsonData);
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to load company email data.");
    } finally {
      updateSpinner?.(false);
    }
  }

  async function enableSystemTemplateAsync(row: CompanySystemEmailTemplate, openEditor = false) {
    if (!company?.id) return;
    updateSpinner?.(true);
    try {
      const resp = await RightTimeApi.postAsync(
        `/EmailTemplate/Company/${company.id}/Override/${row.systemTemplateId}`
      );
      if (resp?.ok && resp.jsonData) {
        toast.success(openEditor ? "Company template ready to customize." : "Template enabled for this company.");
        await loadTabDataAsync();
        if (openEditor) {
          openEdit(resp.jsonData);
        }
      } else {
        toast.error("Failed to enable template for company.");
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to enable template for company.");
    } finally {
      updateSpinner?.(false);
    }
  }

  async function enableExistingCompanyTemplateAsync(template: EmailTemplate) {
    if (!template.id) return;
    updateSpinner?.(true);
    try {
      const resp = await RightTimeApi.putAsync(`/EmailTemplate/${template.id}`, { ...template, isActive: true });
      if (resp?.ok) {
        toast.success("Template enabled for this company.");
        await loadTabDataAsync();
      } else {
        toast.error("Failed to enable template.");
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to enable template.");
    } finally {
      updateSpinner?.(false);
    }
  }

  async function disableCompanyTemplateAsync(template: EmailTemplate) {
    if (!template.id) return;
    updateSpinner?.(true);
    try {
      const resp = await RightTimeApi.deleteAsync(`/EmailTemplate/${template.id}`);
      if (resp?.ok) {
        toast.success("Template disabled for this company.");
        await loadTabDataAsync();
      } else {
        toast.error("Failed to disable template.");
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to disable template.");
    } finally {
      updateSpinner?.(false);
    }
  }

  function openAddCompanyTemplate() {
    setSelectedTemplate({
      id: 0,
      identifier: "",
      name: "",
      subject: "",
      htmlBody: "",
      textBody: "",
      parameterSchema: "[]",
      isActive: true,
      scope: "company",
      companyId: company?.id,
    });
    setIsEditOpen(true);
  }

  function openEdit(template: EmailTemplate) {
    setSelectedTemplate(template);
    setIsEditOpen(true);
  }

  function openSystemTemplateEdit(row: CompanySystemEmailTemplate) {
    if (row.companyTemplate?.id) {
      openEdit(row.companyTemplate);
      return;
    }
    enableSystemTemplateAsync(row, true);
  }

  async function toggleSystemTemplateActive(row: CompanySystemEmailTemplate) {
    if (row.isEnabledForCompany && row.companyTemplate) {
      await disableCompanyTemplateAsync(row.companyTemplate);
      return;
    }
    await enableSystemTemplateAsync(row, false);
  }

  async function toggleCompanyTemplateActive(template: EmailTemplate) {
    if (template.isActive !== false) {
      await disableCompanyTemplateAsync(template);
      return;
    }
    await enableExistingCompanyTemplateAsync(template);
  }

  if (activeTab !== "email" || !company?.id) return null;

  return (
    <>
      <div className="tableWrapper dimensionTable emailTabTemplates emailTabSystemTemplates">
        <table>
          <caption>
            <span className="title">Global Email Templates</span>
          </caption>
          <thead>
            <tr>
              <th>Identifier</th>
              <th>Name</th>
              <th>Subject</th>
              <th>Active</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {(tabData?.systemTemplates || []).length === 0 ? (
              <tr>
                <td colSpan={5}>No global email templates are defined yet.</td>
              </tr>
            ) : (
              (tabData?.systemTemplates || []).map((row) => (
                <tr key={`system-${row.systemTemplateId}`}>
                  <td>{row.identifier}</td>
                  <td>{row.name}</td>
                  <td>{row.subject}</td>
                  <td className="check" onClick={() => toggleSystemTemplateActive(row)}>
                    {row.isEnabledForCompany ? (
                      <MdCheckBox className="checkbox checked actionIcon" title="Active for this company" />
                    ) : (
                      <MdCheckBoxOutlineBlank className="checkbox unchecked actionIcon" title="Inactive for this company" />
                    )}
                  </td>
                  <td className="emailTemplateActionsCell">
                    <CiEdit className="actionIcon" title="Edit" onClick={() => openSystemTemplateEdit(row)} />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="tableWrapper dimensionTable emailTabTemplates emailTabCompanyTemplates">
        <table>
          <caption>
            <span className="title">Company Email Templates</span>
            <span className="actionIcon plusIcon captionRight" onClick={openAddCompanyTemplate} title="Add company template">
              <FaPlusSquare />
            </span>
          </caption>
          <thead>
            <tr>
              <th>Identifier</th>
              <th>Name</th>
              <th>Subject</th>
              <th>Active</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {(tabData?.companyTemplates || []).length === 0 ? (
              <tr>
                <td colSpan={5}>No company-only templates yet. Add one for use on test type email triggers.</td>
              </tr>
            ) : (
              (tabData?.companyTemplates || []).map((template) => (
                <tr key={`company-${template.id}-${template.identifier}`}>
                  <td>{template.identifier}</td>
                  <td>{template.name}</td>
                  <td>{template.subject}</td>
                  <td className="check" onClick={() => toggleCompanyTemplateActive(template)}>
                    {template.isActive !== false ? (
                      <MdCheckBox className="checkbox checked actionIcon" title="Active" />
                    ) : (
                      <MdCheckBoxOutlineBlank className="checkbox unchecked actionIcon" title="Inactive" />
                    )}
                  </td>
                  <td className="emailTemplateActionsCell">
                    <CiEdit className="actionIcon" title="Edit" onClick={() => openEdit(template)} />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="tableWrapper dimensionTable emailTabConfigurations">
        <table>
          <caption>
            <span className="title">Configured Emails</span>
          </caption>
          <thead>
            <tr>
              <th>Test Type</th>
              <th>Trigger</th>
              <th>Template</th>
              <th>Enabled</th>
            </tr>
          </thead>
          <tbody>
            {(tabData?.configurations || []).length === 0 ? (
              <tr>
                <td colSpan={4}>No email triggers configured yet. Configure them on each test type&apos;s Email tab.</td>
              </tr>
            ) : (
              (tabData?.configurations || []).map((row, index) => (
                <tr key={`${row.companyTestTypeId}-${row.triggerKind}-${row.triggerLabel}-${index}`}>
                  <td>{row.companyTestTypeName}</td>
                  <td>
                    {row.triggerKind === "status" ? `Status: ${row.triggerLabel}` : row.triggerLabel}
                  </td>
                  <td>{row.templateName || row.templateIdentifier || "—"}</td>
                  <td>{row.isEnabled ? "Yes" : "No"}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="tableWrapper dimensionTable emailTabRecent">
        <table>
          <caption>
            <span className="title">Recent Emails</span>
          </caption>
          <thead>
            <tr>
              <th>To</th>
              <th>Subject</th>
              <th>Source</th>
              <th>Created</th>
              <th>Sent</th>
              <th>Error</th>
            </tr>
          </thead>
          <tbody>
            {(tabData?.recentEmails || []).length === 0 ? (
              <tr>
                <td colSpan={6}>No emails queued or sent yet.</td>
              </tr>
            ) : (
              (tabData?.recentEmails || []).map((row) => (
                <tr key={row.id}>
                  <td>{row.to}</td>
                  <td>{row.subject}</td>
                  <td>{row.source || "—"}</td>
                  <td>{row.created ? new Date(row.created).toLocaleString() : ""}</td>
                  <td>{row.sent ? new Date(row.sent).toLocaleString() : "Queued"}</td>
                  <td className="emailErrorCell">{row.error || ""}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <EmailTemplateEditModal
        isOpen={isEditOpen}
        template={selectedTemplate}
        companyId={company.id}
        onClose={() => setIsEditOpen(false)}
        onSaved={loadTabDataAsync}
      />
    </>
  );
}
